import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, Truck } from 'lucide-react';

export default function DocumentsPage() {
  const { t, getProductName } = useLanguage();
  const { user, isClient } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (isClient && user?.company_id) {
      fetchData();
    } else {
      fetchAllShipped();
    }
  }, [user]);

  const fetchData = async () => {
    const res = await axios.get(`${API}/orders?company_id=${user.company_id}`);
    setOrders(res.data.filter(o => o.status === 'shipped' || o.status === 'delivered'));
  };

  const fetchAllShipped = async () => {
    const res = await axios.get(`${API}/orders`);
    setOrders(res.data.filter(o => o.status === 'shipped' || o.status === 'delivered'));
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  const generateDoc = (order) => {
    const items = order.order_items?.map(i =>
      `${getProductName(i.product)} - 주문: ${i.ordered_quantity}, 출하: ${i.actual_shipped_quantity || i.ordered_quantity} × ${formatCurrency(i.unit_price)} = ${formatCurrency(i.line_total)}`
    ).join('\n');
    const ref = `${order.company?.client_code}-${order.order_number}`;
    const doc = `
═══════════════════════════════════════════
   CRAFT FOOD FACTORY - SHIPPING DOCUMENT
═══════════════════════════════════════════

주문 번호 / Order Number: ${order.order_number}
참조 / Reference: ${ref}
날짜 / Date: ${new Date(order.order_date).toLocaleDateString()}
배송일 / Shipment Date: ${new Date(order.planned_shipment_date).toLocaleDateString()}

──── CUSTOMER ────
회사 / Company: ${order.company?.company_name}
연락처 / Contact: ${order.company?.contact_person}
전화 / Phone: ${order.company?.phone}

──── DELIVERY ────
수령인 / Recipient: ${order.delivery_location?.recipient_name}
전화 / Phone: ${order.delivery_location?.recipient_phone}
주소 / Address: ${order.delivery_location?.address}

──── ITEMS ────
${items}

──── SUMMARY ────
소계 / Subtotal: ${formatCurrency(order.subtotal)}
배송비 / Delivery: ${formatCurrency(order.delivery_cost)}
합계 / TOTAL: ${formatCurrency(order.total_amount)}

──── PAYMENT INFO ────
은행 / Bank: KB국민은행
계좌 / Account: 123-456-7890
예금주 / Holder: Craft Food Factory
참조 코드 / Payment Reference: ${ref}

═══════════════════════════════════════════
   Thank you for your business!
═══════════════════════════════════════════
`;
    const blob = new Blob([doc], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${order.order_number}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('documents')}</h1>
        <p className="text-gray-500 mt-1">Shipping documents for completed orders</p>
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No documents yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orders.map(o => (
            <Card key={o.id} className="p-5 cff-hover-lift" data-testid={`doc-card-${o.order_number}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-orange-50 p-2 rounded-md">
                  <FileText className="h-5 w-5 text-[#556B2F]" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{o.order_number}</p>
                  <p className="text-xs text-gray-500 cff-text-numeric">
                    {new Date(o.planned_shipment_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="space-y-1 text-sm text-gray-600 mb-4">
                <p>{o.company?.company_name}</p>
                <p className="font-bold cff-text-numeric text-gray-900">{formatCurrency(o.total_amount)}</p>
              </div>
              <Button onClick={() => generateDoc(o)} variant="outline" className="w-full" data-testid={`download-doc-${o.order_number}`}>
                <Download className="h-4 w-4 mr-2" /> Download
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
